<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card class="dt-admin-index-card" elevation="1">
          <v-app-bar elevation="0" color="primary">
            <v-app-bar-title class="text-white text-h6 font-weight-bold pl-2">
              Digital Twin
            </v-app-bar-title>
          </v-app-bar>

          <v-card-text class="pa-6">
            <p class="text-body-2 text-medium-emphasis mb-6">
              Select a site to open its Digital Twin management view.
            </p>
            <v-row align="center">
              <v-col cols="12" sm="5" md="4">
                <v-autocomplete
                  v-model="selectedSite"
                  :items="sites"
                  :loading="isLoading"
                  item-title="siteName"
                  return-object
                  variant="outlined"
                  density="compact"
                  color="primary"
                  bg-color="white"
                  hide-details
                  clearable
                >
                  <template v-slot:label>
                    <span class="text-primary text-caption font-weight-bold">
                      Site
                    </span>
                  </template>
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-title
                        class="text-caption text-medium-emphasis"
                      >
                        No Digital Twin sites available.
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="auto">
                <v-btn
                  color="primary"
                  variant="flat"
                  :disabled="!selectedSite"
                  @click="goToDigitalTwin"
                >
                  Open Digital Twin
                </v-btn>
              </v-col>
            </v-row>

            <v-row v-if="hasLoaded && sites.length === 0" class="mt-2">
              <v-col cols="12" sm="9" md="8">
                <v-alert type="info" variant="tonal" density="compact">
                  No sites with site systems are available. A site system must exist before a
                  Digital Twin can be assigned to it.
                  <v-btn
                    class="ml-2"
                    color="info"
                    variant="text"
                    size="small"
                    @click="goToSiteSystems"
                  >
                    Go to Site Systems
                  </v-btn>
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getDigitalTwinSites } from '../API';
import type { IDigitalTwinSite } from '../types';

const router = useRouter();

const sites = ref<IDigitalTwinSite[]>([]);
const selectedSite = ref<IDigitalTwinSite | null>(null);
const isLoading = ref(false);
const hasLoaded = ref(false);

onMounted(async () => {
  isLoading.value = true;
  try {
    sites.value = await getDigitalTwinSites();
  } finally {
    isLoading.value = false;
    hasLoaded.value = true;
  }
});

function goToSiteSystems(): void {
  router.push({ name: 'SiteSystems' });
}

function goToDigitalTwin(): void {
  if (!selectedSite.value) return;
  router.push({
    name: 'DigitalTwinMVPManagement',
    params: { siteId: selectedSite.value.id || selectedSite.value.siteId },
  });
}
</script>

<style lang="scss" scoped>
.dt-admin-index-card {
  overflow: hidden;
}
</style>
